import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import type { EvolutionDataPointJadlog } from "@/hooks/useImpactDataJadlog";
import { useState } from "react";
import {
  IMPACT_METRIC_COLORS_JADLOG,
  getImpactMetricColorJadlog,
} from "@/utils/impactMetricColorsJadlog";

interface ImpactEvolutionChartJadlogProps {
  data: EvolutionDataPointJadlog[];
}

const METRIC_OPTIONS_JADLOG = [
  {
    value: "co2",
    label: "CO₂ evitado (kg)",
    dataKey: "co2",
    color: IMPACT_METRIC_COLORS_JADLOG.co2,
  },
  {
    value: "energia",
    label: "Energia gerada (kWh)",
    dataKey: "energia",
    color: IMPACT_METRIC_COLORS_JADLOG.energia,
  },
  {
    value: "material",
    label: "Material recuperado (kg)",
    dataKey: "material",
    color: IMPACT_METRIC_COLORS_JADLOG.material,
  },
  {
    value: "aterro",
    label: "Aterro evitado (m³)",
    dataKey: "aterro",
    color: IMPACT_METRIC_COLORS_JADLOG.aterro,
  },
];

export const ImpactEvolutionChartJadlog = ({
  data,
}: ImpactEvolutionChartJadlogProps) => {
  const [selectedMetric, setSelectedMetric] = useState("co2");

  const currentOption =
    METRIC_OPTIONS_JADLOG.find((m) => m.value === selectedMetric) ||
    METRIC_OPTIONS_JADLOG[0];

  const formatYAxis = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    if (value < 1 && value > 0) {
      return value.toFixed(3);
    }
    return value.toString();
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const formattedValue =
        selectedMetric === "aterro"
          ? value.toFixed(3)
          : value.toLocaleString("pt-BR");
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium text-foreground">{label}</p>
          <p className="text-sm" style={{ color: currentOption.color }}>
            {currentOption.label}: {formattedValue}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="bg-card shadow-md hover:shadow-lg transition-shadow">
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
        <div>
          <CardTitle className="text-base font-semibold text-[#10261F]">
            Evolução do impacto no tempo
          </CardTitle>
          <CardDescription className="text-gray-600">
            Acompanhamento mensal dos indicadores ambientais
          </CardDescription>
        </div>
        <Select value={selectedMetric} onValueChange={setSelectedMetric}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {METRIC_OPTIONS_JADLOG.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient
                  id={`colorValue-jadlog-${selectedMetric}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={currentOption.color}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={currentOption.color}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                vertical={false}
              />
              <XAxis
                dataKey="period"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={formatYAxis}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey={currentOption.dataKey}
                stroke={currentOption.color}
                strokeWidth={2}
                fill={`url(#colorValue-jadlog-${selectedMetric})`}
                dot={{ fill: currentOption.color, strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, fill: currentOption.color }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-center mt-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div
              className="w-3 h-0.5 rounded"
              style={{ backgroundColor: currentOption.color }}
            />
            <span>{currentOption.label}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
